// NirmalX pages style their own links (CSS modules), so they use next/link rather than the Chakra-styled Link.
// eslint-disable-next-line no-restricted-imports
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import React from 'react';

import type { Route } from 'nextjs-routes';

import config from 'configs/app';
import AppErrorBoundary from 'ui/shared/AppError/AppErrorBoundary';
import HeaderAlert from 'ui/snippets/header/HeaderAlert';
import SearchBar from 'ui/snippets/searchBar/SearchBar';

import AddToWallet from '../AddToWallet';
import { TICKER, WEBSITE_URL } from '../nirmalx';
import css from './layout.module.css';

const NAV: ReadonlyArray<{ label: string; href: Route; match: RegExp }> = [
  { label: 'Blocks', href: { pathname: '/blocks' }, match: /^\/(blocks|block\/)/ },
  { label: 'Transactions', href: { pathname: '/txs' }, match: /^\/(txs|tx\/|internal-txs|token-transfers)/ },
  { label: 'Tokens', href: { pathname: '/tokens' }, match: /^\/(tokens|token\/)/ },
  { label: 'Contracts', href: { pathname: '/verified-contracts' }, match: /^\/(verified-contracts|contract-verification)/ },
  { label: 'Accounts', href: { pathname: '/accounts' }, match: /^\/accounts/ },
  { label: 'API', href: { pathname: '/api-docs' }, match: /^\/api-docs/ },
];

const icon = config.UI.navigation.icon.dark || config.UI.navigation.icon.default;

const Ticker = () => {
  // Four copies side by side; the strip moves by half its width, so it loops without a seam.
  const items = [ ...TICKER, ...TICKER, ...TICKER, ...TICKER ];
  return (
    <NextLink className={ css.ticker } href={{ pathname: '/', hash: 'horizon' }} aria-label="What's coming to NirmalX">
      <span className={ css.tickerTrack } aria-hidden="true">
        { items.map((text, index) => <span key={ index } className={ css.tickerItem }>{ text }</span>) }
      </span>
    </NextLink>
  );
};

const Brand = () => (
  <NextLink href={{ pathname: '/' }} className={ css.brand } aria-label={ `${ config.chain.name } explorer home` }>
    { icon && <img src={ icon } alt="" className={ css.brandIcon }/> }
    <span className={ css.brandName }>NirmalX</span>
    <span className={ css.brandSub }>Explorer</span>
    { config.chain.isTestnet && <span className={ css.testnetPill }>TESTNET</span> }
  </NextLink>
);

const Header = ({ search }: { search?: React.ReactNode }) => {
  const { asPath } = useRouter();
  const path = asPath.split(/[?#]/)[0];

  return (
    <header className={ css.header }>
      <div className={ `${ css.headerInner } ${ search ? css.headerWithSearch : '' }` }>
        <Brand/>
        { search && <div className={ css.headerSearch }>{ search }</div> }
        <nav className={ css.nav } aria-label="Explorer">
          { NAV.map((item) => (
            <NextLink
              key={ item.label }
              href={ item.href }
              className={ `${ css.navLink } ${ item.match.test(path) ? css.navActive : '' }` }
            >
              { item.label }
            </NextLink>
          )) }
          <a href={ WEBSITE_URL } className={ `${ css.navLink } ${ css.navWebsite }` }>Website ↗</a>
        </nav>
        <AddToWallet
          className={ css.walletButton }
          label={ <><span className={ css.labelLong }>+ Add to wallet</span><span className={ css.labelShort }>+ Wallet</span></> }
        />
      </div>
    </header>
  );
};

const Footer = () => (
  <footer className={ css.footer }>
    <div className={ css.footerInner }>
      <div>
        <Brand/>
        <p className={ css.footerTagline }>
          Rise Beyond Limits. Every block, transaction and address on the { config.chain.name }, sealed live by its
          QBFT validators.
        </p>
      </div>
      <div>
        <div className={ css.footerHeading }>Explore</div>
        <ul className={ css.footerList }>
          { NAV.map((item) => (
            <li key={ item.label }><NextLink href={ item.href }>{ item.label }</NextLink></li>
          )) }
          <li><a href={ WEBSITE_URL }>NirmalX website ↗</a></li>
        </ul>
      </div>
      <div>
        <div className={ css.footerHeading }>Network</div>
        <ul className={ css.footerList }>
          <li className={ css.footerFact }>Chain ID <b>{ config.chain.id }</b></li>
          <li className={ css.footerFact }>Currency <b>{ config.chain.currency.symbol }</b></li>
          { config.chain.rpcUrls[0] && <li className={ css.footerFact }>RPC <b>{ config.chain.rpcUrls[0] }</b></li> }
          <li><AddToWallet className={ css.walletButton }/></li>
        </ul>
      </div>
    </div>
    <div className={ css.footerBottom }>
      © NirmalX. Explorer built on <a href="https://github.com/blockscout/frontend" target="_blank" rel="noopener noreferrer">Blockscout</a> (GPL-3.0).
    </div>
  </footer>
);

interface Props {
  children: React.ReactNode;

  /** `home`: the hero carries the search. `page`: a search bar in the header and a content column. */
  variant: 'home' | 'page';

  /** Replaces the header's search bar (the search results page passes its own input). */
  search?: React.ReactNode;
}

/** The NirmalX shell around every explorer page: announcement strip, header, page, footer. */
const Shell = ({ children, variant, search }: Props) => (
  <div className={ css.page }>
    <Ticker/>
    <Header search={ variant === 'page' ? (search ?? <SearchBar/>) : undefined }/>
    <main className={ `${ css.main } ${ variant === 'page' ? css.mainPage : '' }` }>
      <div className={ css.alert }><HeaderAlert/></div>
      <AppErrorBoundary>
        { variant === 'page' ? <div className={ css.content }>{ children }</div> : children }
      </AppErrorBoundary>
    </main>
    <Footer/>
  </div>
);

export default Shell;
